import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { selectorThemeStyle } from "../config/selectorThemes";
import { SELECTOR_DASHBOARDS } from "../config/selectorDashboards";
import "./dashboardTransition.css";

const TransitionContext = createContext((target, bounds, options) => options?.onNavigate?.());
export const useDashboardTransition = () => useContext(TransitionContext);

export function DashboardHomeLink({ children, ...props }) {
  const begin = useDashboardTransition();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const route = SELECTOR_DASHBOARDS.find(dashboard =>
    location.pathname === dashboard.route || location.pathname.startsWith(`${dashboard.route}/`))?.route || "/mindshare";
  return <>
    <Link {...props} to="/login?menu=1" aria-label="Return to dashboard home" onClick={event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      setError("");
      let cancel;
      cancel = begin("/login", null, {
        direction: "home", route,
        onNavigate: () => {
          try {
            navigate("/login?menu=1", { state: { returnBlade: route } });
          } catch (navigationError) {
            console.error("Dashboard home navigation failed:", navigationError);
            cancel?.();
            setError("Unable to open dashboard home. Please try again.");
          }
        },
      });
    }}>{children}</Link>
    {error && <span role="alert">{error}</span>}
  </>;
}

export default function DashboardTransition({ children }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { currentUser, loadingUser } = useAuth();
  const [transition, setTransition] = useState(null);
  const timer = useRef(null);
  const previousKey = useRef(location.key);

  const clear = useCallback(() => {
    clearTimeout(timer.current);
    setTransition(null);
  }, []);
  const begin = useCallback((target, bounds, options = {}) => {
    clear();
    const route = options.direction === "home" ? options.route : target;
    const index = SELECTOR_DASHBOARDS.findIndex(dashboard => dashboard.route === route);
    if (options.direction === "home" || index < 0 || !currentUser || loadingUser || document.visibilityState !== "visible" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      options.onNavigate?.();
      return clear;
    }
    setTransition({ target, source: location.key, index, direction: options.direction || "enter" });
    timer.current = setTimeout(clear, 280);
    options.onNavigate?.();
    return clear;
  }, [clear, location.key, currentUser, loadingUser]);

  useEffect(() => {
    const popped = navigationType === "POP" && previousKey.current !== location.key;
    previousKey.current = location.key;
    if (!transition) return;
    if (!currentUser || loadingUser || popped || (location.key !== transition.source &&
      location.pathname !== transition.target)) clear();
  }, [location.key, location.pathname, navigationType, transition, currentUser, loadingUser, clear]);
  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState !== "visible") clear(); };
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMotion = () => { if (motion.matches) clear(); };
    document.addEventListener("visibilitychange", onVisibility);
    motion.addEventListener("change", onMotion);
    return () => {
      clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVisibility);
      motion.removeEventListener("change", onMotion);
    };
  }, [clear]);

  const dashboard = transition && SELECTOR_DASHBOARDS[transition.index];
  return (
    <TransitionContext.Provider value={begin}>
      <div className="mg-dashboard-stage">{children}</div>
      {transition && currentUser && !loadingUser && (
        <div key={`${transition.source}:${transition.index}:${transition.direction}`}
          className={`mg-navigation-polish is-${transition.direction}`}
          aria-hidden="true" style={selectorThemeStyle(dashboard.route)}>
          <div className="mg-navigation-signal">
            <span className="mg-navigation-signal-edge mg-navigation-signal-edge-top" />
            <span className="mg-navigation-signal-edge mg-navigation-signal-edge-right" />
            <span className="mg-navigation-signal-edge mg-navigation-signal-edge-bottom" />
            <span className="mg-navigation-signal-edge mg-navigation-signal-edge-left" />
          </div>
        </div>
      )}
    </TransitionContext.Provider>
  );
}
